import type { LngLat } from '@/composables/useLocation'
import type { TelegramLocationData, TelegramLocationManager } from '@/types/telegram'

/** Дольше ждать бессмысленно: клиент поставит точку руками. */
const GEO_TIMEOUT = 10_000

/** `blocked` — доступ уже запрещён, окна не будет; спрашивать бесполезно. */
export type GeoStatus = 'ok' | 'blocked' | 'denied' | 'unavailable' | 'error'

export interface GeoResult {
  status: GeoStatus
  point?: LngLat
}

/**
 * Отклонённое разрешение браузер второй раз не спрашивает — `getCurrentPosition`
 * молча зовёт колбэк ошибки. Знать об этом заранее нужно, чтобы объяснить
 * клиенту, почему окна не появилось.
 */
async function isBrowserBlocked(): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state === 'denied'
  } catch {
    return false
  }
}

/**
 * Настоящий Telegram отличаем по `initData`: скрипт мини-аппа подключён на всех
 * страницах и в обычном браузере тоже создаёт `WebApp` — с пустыми полями.
 */
function telegramManager(): TelegramLocationManager | null {
  const app = window.Telegram?.WebApp
  return app && app.initData !== '' ? (app.LocationManager ?? null) : null
}

/**
 * Колбэки Telegram — не промисы, и в редких случаях не приходят вовсе.
 * Ждать вечно нельзя: на экране висит «Секундочку...», и он бы завис навсегда.
 */
function withTimeout<T>(task: Promise<T>, fallback: T): Promise<T> {
  const timeout = new Promise<T>((resolve) => setTimeout(() => resolve(fallback), GEO_TIMEOUT))
  return Promise.race([task, timeout])
}

/**
 * Внутри Telegram браузерная геолокация не работает: хост-приложение не выдаёт
 * WebView разрешение, поэтому запрос падает мгновенно и без единого окна.
 * Координаты там спрашивает сам Telegram — через `LocationManager`.
 *
 * `null` — мы не в Telegram или клиент старее Bot API 8.0; тогда решает браузер.
 */
async function telegramPoint(): Promise<GeoResult | null> {
  const manager = telegramManager()
  if (!manager) return null

  if (!manager.isInited) {
    await withTimeout(new Promise<void>((resolve) => manager.init(() => resolve())), undefined)
  }
  /* На десктопе GPS нет — сразу отдаём карту, не изображая запрос */
  if (!manager.isLocationAvailable) return { status: 'unavailable' }

  const data = await withTimeout<TelegramLocationData | null>(
    new Promise((resolve) => manager.getLocation(resolve)),
    null,
  )
  if (data) return { status: 'ok', point: [data.longitude, data.latitude] }

  /* Отказавшего второй раз не спрашивают — открываем настройки, там доступ вернуть можно */
  if (manager.isAccessRequested) {
    manager.openSettings()
    return { status: 'blocked' }
  }
  return { status: 'denied' }
}

/** Координаты клиента: сначала пробуем Telegram, потом браузер. */
export async function requestGeoPoint(): Promise<GeoResult> {
  const fromTelegram = await telegramPoint()
  if (fromTelegram) return fromTelegram

  if (!navigator.geolocation) return { status: 'unavailable' }
  if (await isBrowserBlocked()) return { status: 'blocked' }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({ status: 'ok', point: [position.coords.longitude, position.coords.latitude] }),
      (error) => resolve({ status: error.code === error.PERMISSION_DENIED ? 'denied' : 'error' }),
      { enableHighAccuracy: true, timeout: GEO_TIMEOUT },
    )
  })
}
